'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import MemberCard from '@/components/MemberCard';
import { apiGet } from '@/lib/api';

type MemberListItem = {
  id: string;
  displayName: string;
  membershipNumber?: string | null;
  title: string | null;
  profile: string;
  shortIntroduction?: string | null;
  photoUrl?: string | null;
  photoUrls?: import('@/lib/mediaImages').DerivedImageUrls | null;
  photoFocal?: import('@/lib/imageFocal').ImageFocal | null;
  links: Array<{ label: string; url: string }>;
  // 互換性のためにAPIで追加しているが、一覧表示では未使用
  categories?: Array<{ id: string; name: string }>;
};

export default function MembersClient() {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  const keyword = (sp.get('q') ?? '').trim() || undefined;
  const categoryId = (sp.get('categoryId') ?? '').trim() || undefined;
  const sort = sp.get('sort') === 'early' ? 'early' : 'recent';
  const gender =
    sp.get('gender') === 'female'
      ? 'female'
      : sp.get('gender') === 'male'
        ? 'male'
        : undefined;
  const page = Math.max(1, Number(sp.get('page') ?? '1'));
  const pageSize = 12;

  const [items, setItems] = useState<MemberListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setParam = useCallback(
    (params: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(sp.toString());
      Object.entries(params).forEach(([k, v]) => {
        next.delete(k);
        if (v == null || v === '') return;
        next.set(k, v);
      });
      router.push(`${pathname}${next.toString() ? `?${next.toString()}` : ''}`);
    },
    [router, pathname, sp]
  );

  useEffect(() => {
    let cancelled = false;
    /**
     * 目的: URLクエリ(q/categoryId/page)に基づき、会員一覧を非同期で取得して表示する
     * 引数: なし（useSearchParamsの値を参照）
     * 戻り値: Promise<void>
     * 想定される例外:
     * - ネットワークエラー/タイムアウト
     * - APIが 4xx/5xx を返す
     */
    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const qs = new URLSearchParams();
        if (keyword) qs.set('q', keyword);
        if (categoryId) qs.set('categoryId', categoryId);
        if (sort) qs.set('sort', sort);
        if (gender) qs.set('gender', gender);
        qs.set('page', String(page));
        qs.set('pageSize', String(pageSize));

        const data = await apiGet<{ items: MemberListItem[]; total: number }>(
          `/api/members?${qs.toString()}`
        );
        if (cancelled) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(typeof data.total === 'number' ? data.total : 0);
      } catch (e: unknown) {
        console.error('[会員一覧取得エラー]', e);
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setErrorMessage(
          '会員一覧の取得に失敗しました。しばらく時間をおいて再度お試しください。'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [keyword, categoryId, sort, gender, page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const goPage = (n: number) => {
    const next = Math.max(1, Math.min(totalPages, n));
    setParam({ page: next > 1 ? String(next) : null });
  };

  if (errorMessage) {
    return (
      <div className='mx-auto max-w-3xl text-center text-gray-800'>
        <p className='mt-2 text-lg text-red-600'>{errorMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {loading ? (
        <p className='mb-6 text-center text-sm text-gray-500'>読み込み中…</p>
      ) : null}

      <div
        className={`grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 ${
          loading ? 'opacity-60' : ''
        }`}
      >
        {items.map((m) => (
          <MemberCard
            key={m.id}
            name={m.displayName}
            membershipNumber={m.membershipNumber}
            title={m.title}
            photoUrl={m.photoUrl ?? undefined}
            photoUrls={m.photoUrls ?? undefined}
            photoFocal={m.photoFocal ?? undefined}
            profile={m.profile}
            shortIntroduction={m.shortIntroduction ?? undefined}
            id={m.id}
          />
        ))}
      </div>

      {/* Pagination */}
      <nav className='mt-10 flex items-center justify-center gap-2'>
        {page > 1 ? (
          <button
            type='button'
            onClick={() => goPage(page - 1)}
            className='px-3 py-2 text-sm border border-gray-200 bg-white hover:bg-gray-50'
          >
            前へ
          </button>
        ) : null}

        {Array.from({ length: totalPages }).map((_, i) => {
          const n = i + 1;
          const active = n === page;
          return (
            <button
              key={n}
              type='button'
              onClick={() => goPage(n)}
              aria-current={active ? 'page' : undefined}
              className={`px-3 py-2 text-sm ${
                active
                  ? 'bg-primary text-white'
                  : 'border border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              {n}
            </button>
          );
        })}

        {page < totalPages ? (
          <button
            type='button'
            onClick={() => goPage(page + 1)}
            className='px-3 py-2 text-sm border border-gray-200 bg-white hover:bg-gray-50'
          >
            次へ
          </button>
        ) : null}
      </nav>
    </div>
  );
}

